import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { Staff } from '../interfaces';

/**
 * Sucht ein Steuerelement im ganzen Formularbaum, unabhaengig davon, wie tief
 * es verschachtelt ist. Die Formulare halten `employee_phm` teils direkt, teils
 * in einer Stufengruppe; die Suche macht die Aufrufstelle davon unabhaengig.
 */
function findeSteuerelement(control: AbstractControl | null, name: string): AbstractControl | null {
  if (!control) {
    return null;
  }
  if (control instanceof FormGroup) {
    const direkt = control.controls[name];
    if (direkt) {
      return direkt;
    }
    for (const key of Object.keys(control.controls)) {
      const treffer = findeSteuerelement(control.controls[key], name);
      if (treffer) {
        return treffer;
      }
    }
  }
  if (control instanceof FormArray) {
    for (const child of control.controls) {
      const treffer = findeSteuerelement(child, name);
      if (treffer) {
        return treffer;
      }
    }
  }
  return null;
}

/**
 * Ergebnisse je Quellliste und Zuweisung.
 *
 * Die Formulare rufen `sichtbareMitarbeiter` aus einem Getter heraus auf, und
 * Angular wertet Getter bei jedem Change-Detection-Lauf aus. Ohne diesen
 * Speicher entstuende bei jedem Lauf ein neues Array -- und fuer den passiven
 * Eintrag ein neues Objekt, weil er kopiert wird. `*ngFor` vergleicht die
 * Eintraege ueber ihre Identitaet und baute diese Zeile deshalb staendig neu
 * auf. Der WeakMap-Schluessel ist die Quellliste selbst; sie wird je Ladevorgang
 * genau einmal gesetzt, der Eintrag verschwindet also mit ihr.
 */
const speicher = new WeakMap<Staff[], Map<number, Staff[]>>();

/**
 * Die Mitarbeiter, die in einer Auswahl erscheinen duerfen.
 *
 * Regel: nur aktive. Mit einer Ausnahme, die es braucht -- **der aktuell
 * zugewiesene Mitarbeiter bleibt in der Liste, auch wenn er passiv ist.**
 *
 * Ohne diese Ausnahme waere aus dem Aufraeumen ein Datenverlust geworden: im
 * PHM-Bestand hingen am 2026-08-24 rund 5.300 Vorgaenge an inzwischen passiven
 * Mitarbeitern (Kuehnl, Milz, Rachler, Behrendt, Hoffmann). Beim Oeffnen eines
 * solchen Vorgangs enthielte eine streng gefilterte Liste den gesetzten Wert
 * nicht mehr. Die Radiogruppe zeigte dann nichts ausgewaehlt -- es saehe aus,
 * als sei kein Bearbeiter hinterlegt -- und der erste Klick auf einen anderen
 * Namen haette die historische Zuordnung ueberschrieben.
 *
 * Der passive Eintrag wird mit "(passiv)" gekennzeichnet. Das geschieht auf
 * einer Kopie und nur fuer die Anzeige: gespeichert wird ohnehin nur die
 * `staff_id`. So bleiben die neun Formularvorlagen unveraendert, statt die
 * Kennzeichnung neunmal einzeln zu pflegen.
 */
export function sichtbareMitarbeiter(
  alle: Staff[],
  form: AbstractControl | null,
  controlName: string = 'employee_phm'
): Staff[] {
  const roh = findeSteuerelement(form, controlName)?.value;
  const zugewiesen = Number(roh);
  const hatZuweisung = Number.isFinite(zugewiesen) && zugewiesen > 0;
  const schluessel = hatZuweisung ? zugewiesen : 0;

  let jeZuweisung = speicher.get(alle);
  if (!jeZuweisung) {
    jeZuweisung = new Map<number, Staff[]>();
    speicher.set(alle, jeZuweisung);
  }

  const bekannt = jeZuweisung.get(schluessel);
  if (bekannt) {
    return bekannt;
  }

  const liste = alle
    .filter((m) => m.active !== false || (hatZuweisung && m.staff_id === zugewiesen))
    .map((m) =>
      m.active === false ? { ...m, lastname: `${m.lastname} (passiv)` } : m
    );

  jeZuweisung.set(schluessel, liste);
  return liste;
}
